import { Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { finalize } from 'rxjs';
import { PaginatorState } from 'primeng/paginator';
import { MessageService } from 'primeng/api';

import { KardexAuditProxyService } from '../../../proxies/inventory/kardex-audit.proxy.service';
import {
  KardexTransactionSearchCommand,
  KardexTransaction,
  KardexTransactionLine,
  KardexTransactionType,
  KardexLineItemType,
} from '../../../models/kardex-audit/kardex-audit';

interface SelectOption {
  label: string;
  value: number;
}

interface SiteOption {
  label: string;
  value: string;
}

interface SearchForm {
  fromDate: FormControl<Date | null>;
  toDate: FormControl<Date | null>;
  itemCode: FormControl<string>;
  transactionTypes: FormControl<number[]>;
  site: FormControl<string>;
  lotItemNumber: FormControl<string>;
  productionCardNumber: FormControl<string>;
  location: FormControl<string>;
}

@Component({
  selector: 'app-kardex-audit-dashboard',
  templateUrl: './kardex-audit-dashboard.component.html',
  styleUrls: ['./kardex-audit-dashboard.component.scss'],
})
export class KardexAuditDashboardComponent implements OnInit {
  private readonly proxyService = inject(KardexAuditProxyService);
  private readonly messageService = inject(MessageService);

  // State signals
  isLoading = signal<number>(0);
  isExporting = signal(false);
  transactions = signal<KardexTransaction[]>([]);
  totalRecords = 0;

  // Pagination
  currentPage = 1;
  pageSize = 50;

  // Row expansion state
  expandedTxRows: Record<string, boolean> = {};

  // Roll history dialog
  selectedRollNumber = signal<string | null>(null);
  selectedTransactionId = signal<string | null>(null);
  showRollHistoryDialog = signal(false);

  // Filter form
  searchForm: FormGroup<SearchForm>;

  transactionTypeOptions: SelectOption[] = [
    { label: 'Transfer', value: KardexTransactionType.Transfer },
    { label: 'Receipt', value: KardexTransactionType.Receipt },
    { label: 'Adjustment', value: KardexTransactionType.Adjustment },
    { label: 'Consumption', value: KardexTransactionType.Consumption },
    { label: 'Internal Return', value: KardexTransactionType.InternalReturn },
    { label: 'Provider Return', value: KardexTransactionType.ProviderReturn },
    { label: 'Internal Transfer', value: KardexTransactionType.InternalTransfer },
    { label: 'Initial Load', value: KardexTransactionType.InitialLoad },
  ];

  // TODO multi-site: replace location-prefix options with SiteIds from the site context
  // once feature/multi-site reaches the UI.
  siteOptions: SiteOption[] = [
    { label: 'All Sites', value: '' },
    { label: 'Nexus Textiles', value: 'NT.' },
    { label: 'SIMTEX', value: 'SIMTEX.' },
    { label: 'Pride', value: 'Pride.' },
  ];

  private readonly transactionTypeLabels = new Map<number, string>(this.transactionTypeOptions.map((o) => [o.value, o.label]));

  private readonly statusLabels = new Map<number, string>([
    [0, 'Draft'],
    [1, 'In Route'],
    [2, 'Received'],
    [3, 'Rejected'],
    [4, 'In Route Back'],
    [5, 'Voided'],
    [6, 'Ready To Transfer'],
  ]);

  constructor() {
    // Dates default empty so every filter is independent and organic: a roll, item or
    // production card search is never silently constrained by a leftover date range.
    this.searchForm = new FormGroup<SearchForm>({
      fromDate: new FormControl<Date | null>(null),
      toDate: new FormControl<Date | null>(null),
      itemCode: new FormControl<string>('', { nonNullable: true }),
      transactionTypes: new FormControl<number[]>([], { nonNullable: true }),
      site: new FormControl<string>('', { nonNullable: true }),
      lotItemNumber: new FormControl<string>('', { nonNullable: true }),
      productionCardNumber: new FormControl<string>('', { nonNullable: true }),
      location: new FormControl<string>('', { nonNullable: true }),
    });
  }

  ngOnInit(): void {
    this.search();
  }

  private buildCommand(): KardexTransactionSearchCommand {
    const { fromDate, toDate, itemCode, transactionTypes, site, lotItemNumber, productionCardNumber, location } =
      this.searchForm.getRawValue();

    let fromIso: string | undefined;
    if (fromDate) {
      const from = new Date(fromDate);
      from.setHours(0, 0, 0, 0);
      fromIso = from.toISOString();
    }

    let toIso: string | undefined;
    if (toDate) {
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999);
      toIso = to.toISOString();
    }

    return {
      fromDate: fromIso,
      toDate: toIso,
      itemCode: itemCode?.trim() || undefined,
      transactionTypes: transactionTypes?.length ? transactionTypes : undefined,
      locationCodePrefix: site || undefined,
      lotItemNumber: lotItemNumber?.trim() || undefined,
      productionCardNumber: productionCardNumber?.trim() || undefined,
      location: location?.trim() || undefined,
    };
  }

  search(): void {
    this.isLoading.update((v) => v + 1);
    this.proxyService
      .search(this.buildCommand(), this.currentPage, this.pageSize)
      .pipe(finalize(() => this.isLoading.update((v) => v - 1)))
      .subscribe({
        next: (result) => {
          this.transactions.set(result?.data?.items || []);
          this.totalRecords = result?.data?.totalCount || 0;
        },
        error: () => {
          this.transactions.set([]);
          this.totalRecords = 0;
        },
      });
  }

  onSearch(): void {
    this.currentPage = 1;
    this.expandedTxRows = {};
    this.search();
  }

  onClear(): void {
    this.searchForm.reset({
      fromDate: null,
      toDate: null,
      itemCode: '',
      transactionTypes: [],
      site: '',
      lotItemNumber: '',
      productionCardNumber: '',
      location: '',
    });
    this.onSearch();
  }

  onExport(): void {
    this.isExporting.set(true);
    this.proxyService
      .exportToExcel(this.buildCommand())
      .pipe(finalize(() => this.isExporting.set(false)))
      .subscribe({
        next: (blob) => {
          this.downloadBlob(blob, 'kardex-audit-export.xlsx');
        },
      });
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
    this.messageService.add({
      key: 'br',
      severity: 'success',
      summary: 'Export Complete',
      detail: `Downloaded ${filename}`,
      life: 3000,
    });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage = (event.page || 0) + 1;
    this.pageSize = event.rows || this.pageSize;
    this.expandedTxRows = {};
    this.search();
  }

  toggleTxRow(txId: string): void {
    this.expandedTxRows[txId] = !this.expandedTxRows[txId];
  }

  isTxExpanded(txId: string): boolean {
    return !!this.expandedTxRows[txId];
  }

  openRollHistory(rollNumber: string, transactionId: string): void {
    this.selectedRollNumber.set(rollNumber);
    this.selectedTransactionId.set(transactionId);
    this.showRollHistoryDialog.set(true);
  }

  closeRollHistory(): void {
    this.showRollHistoryDialog.set(false);
    this.selectedRollNumber.set(null);
    this.selectedTransactionId.set(null);
  }

  transactionTypeLabel(type: number): string {
    return this.transactionTypeLabels.get(type) ?? `Type (${type})`;
  }

  statusLabel(status: number): string {
    return this.statusLabels.get(status) ?? `Status (${status})`;
  }

  isRollLine(line: KardexTransactionLine): boolean {
    return line.lineItemType === KardexLineItemType.Roll;
  }

  shortId(id: string): string {
    return id?.length > 6 ? id.slice(-6) : id;
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text);
  }

  txStrict(item: unknown): KardexTransaction {
    return item as KardexTransaction;
  }

  lineStrict(item: unknown): KardexTransactionLine {
    return item as KardexTransactionLine;
  }
}
